import React from "react";
import { LOGO_URL } from "../../lib/brand";
import { calcJob, money, formatDate } from "../../lib/jobCalc";
import { COMPANY } from "../../lib/jobConstants";

/* All six document templates rendered from a single Job record.
 * Print-styled — when the user hits "Print / Save as PDF" the active doc
 * fills the page and everything else is hidden via CSS rules in index.css.
 */

const DocFrame = ({ title, children, docKey, voided }) => (
  <div className={`bg-white border border-[#cfd7db] rounded-md shadow-sm doc-frame doc-${docKey} ${voided ? "voided-doc" : ""}`} data-testid={`doc-${docKey}`}>
    <div className="px-8 py-6 border-b border-[#eef2f4] flex items-center justify-between gap-6">
      <div>
        <img src={LOGO_URL} alt="Diesel & Dust" className="h-16 w-auto" />
      </div>
      <div className="text-right">
        <div className="font-display font-black uppercase text-2xl text-[#111619]">{title}</div>
        <div className="text-xs text-[#3f4b52] mt-1">{COMPANY.name} · {COMPANY.tradingAs}</div>
        <div className="text-xs text-[#3f4b52]">{COMPANY.email} · {COMPANY.phone}</div>
      </div>
    </div>
    <div className="p-8">{children}</div>
  </div>
);

const Row = ({ label, value, span = 1 }) => (
  <div className={`flex flex-col ${span === 2 ? "sm:col-span-2" : ""}`}>
    <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">{label}</div>
    <div className="text-sm text-[#111619] mt-0.5 border-b border-[#cfd7db] pb-1.5 min-h-[24px]">
      {value || <span className="text-[#cfd7db]">—</span>}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="font-display uppercase tracking-wider text-sm text-white bg-[#20282c] px-3 py-1.5 rounded-sm mt-6 mb-4">
    {children}
  </div>
);

const Grid = ({ children, cols = 2 }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-x-6 gap-y-3`}>{children}</div>
);

const Banking = () => (
  <div className="mt-8 pt-4 border-t border-[#cfd7db]">
    <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-2">Banking Details</div>
    <div className="text-xs text-[#3f4b52]">
      <div>{COMPANY.bank}: <b>{COMPANY.name}</b> · Branch Code: {COMPANY.branch}</div>
      <div className="mt-1">Please send proof of payment to: <b>{COMPANY.email}</b></div>
    </div>
  </div>
);

// --- Individual documents ---

const JobCard = ({ job, calc }) => (
  <DocFrame title="Job Card" docKey="job_card" voided={job.voided}>
    <div className="flex justify-between items-center mb-4">
      <div className="font-display text-sm">Job number: <b>#{job.job_number}</b></div>
      <div className="font-display text-sm">Date: <b>{formatDate(new Date().toISOString().slice(0,10))}</b></div>
    </div>
    <SectionTitle>Client Details</SectionTitle>
    <Grid>
      <Row label="Client / Company" value={job.client_name} />
      <Row label="ID / Company No" value={job.client_id_number} />
      <Row label="Mobile" value={job.contact_mobile} />
      <Row label="Email" value={job.email} />
      <Row label="Home" value={job.contact_home} />
      <Row label="Work" value={job.contact_work} />
      <Row label="Fitment address" value={job.fitment_address} span={2} />
      <Row label="GPS" value={job.fitment_gps} span={2} />
    </Grid>

    <SectionTitle>Vehicle Details</SectionTitle>
    <Grid>
      <Row label="Vehicle" value={job.vehicle_make_model} />
      <Row label="Year" value={job.vehicle_year} />
      <Row label="Registration" value={job.registration} />
      <Row label="Engine no" value={job.engine_number} />
      <Row label="VIN no" value={job.vin_number} />
      <Row label="Part code" value={job.part_code} />
    </Grid>

    {job.payment_type === "insurance" && (
      <>
        <SectionTitle>Insurance & Policy</SectionTitle>
        <Grid>
          <Row label="Insurance company" value={job.insurance_company} />
          <Row label="Broker" value={job.broker} />
          <Row label="Broker contact" value={job.broker_contact} />
          <Row label="Authorised by" value={job.authorized_by} />
          <Row label="Policy number" value={job.policy_number} />
          <Row label="Claim number" value={job.claim_number} />
          <Row label="Date of loss" value={formatDate(job.date_of_loss)} />
          <Row label="Cause of loss" value={job.cause_of_loss} />
        </Grid>
      </>
    )}

    <SectionTitle>Fitment Scheduling</SectionTitle>
    <Grid>
      <Row label="Fitment date" value={formatDate(job.fitment_date)} />
      <Row label="Fitment time" value={job.fitment_time} />
      <Row label="Fitter / team leader" value={job.fitter_team_leader} />
      <Row label="Assistant" value={job.assistant} />
    </Grid>

    <SectionTitle>Costing</SectionTitle>
    <table className="w-full text-sm mt-2">
      <tbody>
        <tr><td className="py-1.5 text-[#3f4b52]">Cost</td><td className="py-1.5 text-right">{money(calc.cost)}</td></tr>
        <tr><td className="py-1.5 text-[#3f4b52]">Retail</td><td className="py-1.5 text-right">{money(calc.retail)}</td></tr>
        <tr><td className="py-1.5 text-[#3f4b52]">Fitment</td><td className="py-1.5 text-right">{money(calc.fitment)}</td></tr>
        <tr><td className="py-1.5 text-[#3f4b52]">Moulding</td><td className="py-1.5 text-right">{money(calc.moulding)}</td></tr>
        <tr><td className="py-1.5 text-[#3f4b52]">Subtotal</td><td className="py-1.5 text-right">{money(calc.subAfterDiscount)}</td></tr>
        <tr><td className="py-1.5 text-[#3f4b52]">VAT ({job.vat_pct}%)</td><td className="py-1.5 text-right">{money(calc.vatAmount)}</td></tr>
        <tr className="border-t border-[#cfd7db]"><td className="py-1.5 font-bold">Inclusive subtotal</td><td className="py-1.5 text-right font-bold">{money(calc.inclusiveSubtotal)}</td></tr>
        {job.payment_type === "insurance" && (
          <>
            <tr><td className="py-1.5 text-[#3f4b52]">Excess ({job.excess_pct}%)</td><td className="py-1.5 text-right">{money(calc.excessAmount)}</td></tr>
            <tr><td className="py-1.5 font-bold">Insurance total</td><td className="py-1.5 text-right font-bold">{money(calc.insuranceTotal)}</td></tr>
          </>
        )}
        <tr><td className="py-1.5 text-[#10b981]">Gross profit</td><td className="py-1.5 text-right text-[#10b981]">{money(calc.grossProfit)}</td></tr>
      </tbody>
    </table>

    {job.notes && (
      <>
        <SectionTitle>Notes</SectionTitle>
        <p className="text-sm text-[#111619] whitespace-pre-wrap">{job.notes}</p>
      </>
    )}
  </DocFrame>
);

const LOC_LABELS = {
  windscreen: "Windscreen",
  rear_window: "Rear window",
  cabslider: "Cabslider",
  door_driver_front: "Door glass — driver front",
  door_driver_rear: "Door glass — driver rear",
  door_pass_front: "Door glass — pass. front",
  door_pass_rear: "Door glass — pass. rear",
  flatglass: "Flatglass",
};
const ACT_LABELS = { replace: "Replace", repair: "Repair" };

const FlatglassPreview = ({ items = [] }) => {
  const filled = items.filter(
    (it) => it && (it.label || it.width || it.length || it.notes || it.price),
  );
  if (!filled.length) return null;
  const totals = filled.reduce(
    (acc, it) => acc + (Number(it.qty) || 0) * (Number(it.price) || 0),
    0,
  );
  const fmt = (n) =>
    "R " + (Number(n) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <>
      <SectionTitle>Flatglass items</SectionTitle>
      <table className="w-full text-xs border border-[#cfd7db] mb-4" data-testid="preview-flatglass-table">
        <thead>
          <tr className="bg-[#111619] text-white">
            <th className="text-left px-2 py-1.5 font-display uppercase tracking-widest">Label</th>
            <th className="text-left px-2 py-1.5 font-display uppercase tracking-widest">Width</th>
            <th className="text-left px-2 py-1.5 font-display uppercase tracking-widest">Length</th>
            <th className="text-left px-2 py-1.5 font-display uppercase tracking-widest">Qty</th>
            <th className="text-right px-2 py-1.5 font-display uppercase tracking-widest">Unit price</th>
            <th className="text-right px-2 py-1.5 font-display uppercase tracking-widest">Total</th>
            <th className="text-left px-2 py-1.5 font-display uppercase tracking-widest">Notes</th>
          </tr>
        </thead>
        <tbody>
          {filled.map((it, i) => {
            const q = Number(it.qty) || 0;
            const p = Number(it.price) || 0;
            const lt = q * p;
            return (
              <tr key={i} className="border-t border-[#eef2f4]">
                <td className="px-2 py-1.5">{it.label || "—"}</td>
                <td className="px-2 py-1.5">{it.width || "—"}</td>
                <td className="px-2 py-1.5">{it.length || "—"}</td>
                <td className="px-2 py-1.5">{it.qty || "1"}</td>
                <td className="px-2 py-1.5 text-right">{p ? fmt(p) : "—"}</td>
                <td className="px-2 py-1.5 text-right">{p ? fmt(lt) : "—"}</td>
                <td className="px-2 py-1.5">{it.notes || ""}</td>
              </tr>
            );
          })}
          {totals > 0 && (
            <tr className="border-t border-[#cfd7db] bg-[#edf2f4] font-bold">
              <td className="px-2 py-1.5" colSpan={4}></td>
              <td className="px-2 py-1.5 text-right">Flatglass total</td>
              <td className="px-2 py-1.5 text-right">{fmt(totals)}</td>
              <td></td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
};

const DamageRepairPreview = ({ job }) => {
  const locs = (job.damage_locations || []).map((c) => LOC_LABELS[c] || c);
  const acts = (job.claim_actions || []).map((c) => ACT_LABELS[c] || c);
  const cause = (job.cause_of_loss || "").trim();
  if (!locs.length && !acts.length && !cause) return null;
  const n = Math.max(locs.length, acts.length, 1);
  return (
    <>
      <SectionTitle>Damage & Repair Type</SectionTitle>
      <table className="w-full text-xs border border-[#cfd7db] mb-4">
        <thead>
          <tr className="bg-[#111619] text-white">
            <th className="text-left px-2 py-1.5 font-display uppercase tracking-widest">Damage location</th>
            <th className="text-left px-2 py-1.5 font-display uppercase tracking-widest">Action</th>
            <th className="text-left px-2 py-1.5 font-display uppercase tracking-widest">Loss cause</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: n }).map((_, i) => (
            <tr key={i} className="border-t border-[#eef2f4]">
              <td className="px-2 py-1.5">{locs[i] || ""}</td>
              <td className="px-2 py-1.5">{acts[i] || ""}</td>
              <td className="px-2 py-1.5">{i === 0 ? (cause || "—") : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const ClaimForm = ({ job, calc }) => (
  <DocFrame title="Insurance Claim Form" docKey="claim_form" voided={job.voided}>
    <Grid>
      <Row label="To" value={job.insurance_company} />
      <Row label="Attention" value="Claims" />
      <Row label="Date" value={formatDate(new Date().toISOString().slice(0,10))} />
      <Row label="Insured" value={job.client_name} />
      <Row label="Policy number" value={job.policy_number} />
      <Row label="Claim number" value={job.claim_number} />
      <Row label="Contact number" value={job.contact_mobile} />
      <Row label="Authorised by" value={job.authorized_by} />
      <Row label="Estimated excess" value={money(calc.excessAmount)} />
    </Grid>

    <SectionTitle>Vehicle & Replacement Details</SectionTitle>
    <Grid>
      <Row label="Make" value={job.vehicle_make_model} />
      <Row label="Year" value={job.vehicle_year} />
      <Row label="Registration" value={job.registration} />
      <Row label="Engine no" value={job.engine_number} />
      <Row label="VIN no" value={job.vin_number} />
      <Row label="Date fitted" value={formatDate(job.fitment_date)} />
      <Row label="Date of loss" value={formatDate(job.date_of_loss)} />
      <Row label="Cause of loss" value={job.cause_of_loss} />
    </Grid>

    <DamageRepairPreview job={job} />

    {(job.damage_locations || []).includes("flatglass") && (
      <FlatglassPreview items={job.flatglass_items || []} />
    )}

    <SectionTitle>Client Details</SectionTitle>
    <Grid>
      <Row label="Client / company name" value={job.client_name} span={2} />
      <Row label="Title & surname" value={job.client_title_surname || job.client_name} span={2} />
      <Row label="ID number" value={job.client_id_number} />
      <Row label="Contact" value={job.contact_mobile} />
      <Row label="Fitment address" value={job.fitment_address} span={2} />
    </Grid>

    <div className="mt-10 grid grid-cols-2 gap-10">
      <div>
        <div className="border-b border-[#111619] h-10" />
        <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mt-1">Client signature</div>
      </div>
      <div>
        <div className="border-b border-[#111619] h-10" />
        <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mt-1">Date</div>
      </div>
    </div>

    <div className="mt-8 text-[11px] text-[#3f4b52] leading-relaxed">
      <b>Terms & Conditions (summary):</b> Your windscreen requires time to bond correctly to the vehicle body
      (safe drive-away 120–240 min). Ensure all valuables are removed before fitment. By accepting these terms
      the client is personally responsible for all monies due. Outstanding accounts may be handed over with
      collection fees recoverable under South African law. Full terms available on our website.
    </div>
  </DocFrame>
);

const InsuranceQuotation = ({ job, calc }) => (
  <DocFrame title={`Insurance Quotation${job.quotation_number ? ` · ${job.quotation_number}` : ""}`} docKey="insurance_quotation" voided={job.voided}>
    <Grid>
      <Row label="Quotation No." value={job.quotation_number || "—"} />
      <Row label="To" value={job.insurance_company} />
      <Row label="Attention" value="Claims" />
      <Row label="Date" value={formatDate(new Date().toISOString().slice(0,10))} />
      <Row label="Insured" value={job.client_name} />
      <Row label="Policy number" value={job.policy_number} />
      <Row label="Claim number" value={job.claim_number} />
      <Row label="Date of loss" value={formatDate(job.date_of_loss)} />
      <Row label="Broker" value={job.broker} />
      <Row label="Contact" value={job.broker_contact} />
      <Row label="Authorised by" value={job.authorized_by} />
    </Grid>

    <SectionTitle>Vehicle & Replacement Details</SectionTitle>
    <Grid>
      <Row label="Make" value={job.vehicle_make_model} />
      <Row label="Year" value={job.vehicle_year} />
      <Row label="Registration" value={job.registration} />
      <Row label="Date fitted" value={formatDate(job.fitment_date)} />
      <Row label="Part code" value={job.part_code} span={2} />
    </Grid>

    <table className="w-full text-sm mt-6">
      <tbody>
        <tr><td className="py-1.5 text-[#3f4b52]">Retail</td><td className="py-1.5 text-right">{money(calc.retail)}</td></tr>
        <tr><td className="py-1.5 text-[#3f4b52]">Moulding</td><td className="py-1.5 text-right">{money(calc.moulding)}</td></tr>
        <tr><td className="py-1.5 text-[#3f4b52]">Fitment</td><td className="py-1.5 text-right">{money(calc.fitment)}</td></tr>
        {(job.extra_line_items || []).filter((l) => !l.is_discount).map((l, i) => (
          <tr key={`ex-${i}`}><td className="py-1.5 text-[#3f4b52]">{l.description || "Extra"}</td><td className="py-1.5 text-right">{money((l.qty || 1) * (l.unit_price || 0))}</td></tr>
        ))}
        <tr className="border-t border-[#cfd7db]"><td className="py-1.5">Subtotal</td><td className="py-1.5 text-right">{money(calc.grossLineSubtotal)}</td></tr>
        {job.discount_pct > 0 && (
          <tr><td className="py-1.5 text-[#3f4b52]">Discount ({job.discount_pct}%)</td><td className="py-1.5 text-right">-{money(calc.grossLineSubtotal * job.discount_pct / 100)}</td></tr>
        )}
        {(job.extra_line_items || []).filter((l) => l.is_discount).map((l, i) => (
          <tr key={`dx-${i}`}><td className="py-1.5 text-[#3f4b52]">{l.description || "Discount"}</td><td className="py-1.5 text-right">-{money(Math.abs((l.qty || 1) * (l.unit_price || 0)))}</td></tr>
        ))}
        <tr><td className="py-1.5 text-[#3f4b52]">VAT {job.vat_mode === "inclusive" ? "(incl.)" : job.vat_mode === "none" ? "(none)" : `(${job.vat_pct}%)`}</td><td className="py-1.5 text-right">{money(calc.vatAmount)}</td></tr>
        <tr className="border-t border-[#cfd7db]"><td className="py-1.5">Inclusive subtotal</td><td className="py-1.5 text-right">{money(calc.inclusiveSubtotal)}</td></tr>
        <tr><td className="py-1.5 text-[#3f4b52]">Excess ({job.excess_pct}%)</td><td className="py-1.5 text-right">{money(calc.excessAmount)}</td></tr>
        <tr className="border-t-2 border-[#111619]"><td className="py-2 font-display uppercase tracking-wide font-bold">Total payable by insurer</td><td className="py-2 text-right font-bold text-lg text-[#f28a16]">{money(calc.insuranceTotal)}</td></tr>
      </tbody>
    </table>
    <Banking />
  </DocFrame>
);

const ExcessInvoice = ({ job, calc }) => (
  <DocFrame title="Excess Invoice" docKey="excess_invoice" voided={job.voided}>
    <Grid>
      <Row label="To" value={job.client_name} />
      <Row label="Insurance" value={job.insurance_company} />
      <Row label="Policy number" value={job.policy_number} />
      <Row label="Claim number" value={job.claim_number} />
      <Row label="Date of loss" value={formatDate(job.date_of_loss)} />
      <Row label="Broker" value={job.broker} />
      <Row label="Contact" value={job.broker_contact} />
      <Row label="Authorised by" value={job.authorized_by} />
    </Grid>

    <SectionTitle>Vehicle & Replacement Details</SectionTitle>
    <Grid>
      <Row label="Make" value={job.vehicle_make_model} />
      <Row label="Year" value={job.vehicle_year} />
      <Row label="Registration" value={job.registration} />
      <Row label="Date fitted" value={formatDate(job.fitment_date)} />
      <Row label="Cause of loss" value={job.cause_of_loss} span={2} />
      <Row label="Part code" value={job.part_code} span={2} />
    </Grid>

    <table className="w-full text-sm mt-6">
      <tbody>
        <tr><td className="py-1.5 text-[#3f4b52]">Inclusive subtotal</td><td className="py-1.5 text-right">{money(calc.inclusiveSubtotal)}</td></tr>
        <tr><td className="py-1.5 text-[#3f4b52]">Excess percentage</td><td className="py-1.5 text-right">{job.excess_pct}%</td></tr>
        <tr className="border-t-2 border-[#111619]"><td className="py-2 font-display uppercase tracking-wide font-bold">Excess due by client</td><td className="py-2 text-right font-bold text-lg text-[#f28a16]">{money(calc.excessAmount)}</td></tr>
      </tbody>
    </table>
    <Banking />
  </DocFrame>
);

const OrderForm = ({ job }) => (
  <DocFrame title="Order Form" docKey="order_form" voided={job.voided}>
    <div className="text-xs text-[#3f4b52] mb-3">
      With every windscreen replacement the order form must be completed and signed.
    </div>
    <Grid>
      <Row label="Order number" value={job.order_number} />
      <Row label="Date" value={formatDate(new Date().toISOString().slice(0,10))} />
    </Grid>

    <SectionTitle>Vehicle & Replacement Details</SectionTitle>
    <Grid>
      <Row label="Make" value={job.vehicle_make_model} />
      <Row label="Year" value={job.vehicle_year} />
      <Row label="Registration" value={job.registration} />
      <Row label="Engine no" value={job.engine_number} />
      <Row label="VIN no" value={job.vin_number} />
      <Row label="Date fitted" value={formatDate(job.fitment_date)} />
      <Row label="Cause of loss" value={job.cause_of_loss} span={2} />
      <Row label="Part code" value={job.part_code} span={2} />
    </Grid>

    <SectionTitle>Comments</SectionTitle>
    <div className="border border-[#cfd7db] rounded-sm min-h-[80px] p-3 text-sm whitespace-pre-wrap">
      {job.notes || <span className="text-[#cfd7db]">—</span>}
    </div>

    <div className="mt-10 grid grid-cols-2 gap-10">
      <div>
        <div className="border-b border-[#111619] h-10" />
        <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mt-1">Client signature</div>
      </div>
      <div>
        <div className="border-b border-[#111619] h-10" />
        <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mt-1">Date</div>
      </div>
    </div>
    <Banking />
  </DocFrame>
);

const TaxInvoice = ({ job, calc }) => {
  const isInsurance = job.payment_type === "insurance";
  const isIssued = Boolean(job.tax_invoice_number);
  // For insurance the invoice bills the insurer for the amount payable by insurer
  // (gross minus client excess). For cash the client pays the full gross.
  const totalDue = isInsurance ? calc.insuranceTotal : calc.inclusiveSubtotal;
  const billTo = isInsurance ? (job.insurance_company || "Insurance company") : (job.client_name || "—");
  const invRef = job.tax_invoice_number || job.invoice_number || "PRO-FORMA";

  return (
    <DocFrame
      title={`Tax Invoice · ${invRef}`}
      docKey="tax_invoice"
      voided={job.voided}
    >
      {!isIssued && (
        <div className="mb-4 bg-[#fff7ed] border border-[#fb923c] text-[#7c2d12] text-xs px-3 py-2 rounded-md">
          <b>Pro-forma preview.</b> This will become the official Tax Invoice once you click <b>Issue Tax Invoice</b>. A sequential INV number will be locked in.
        </div>
      )}
      <Grid>
        <Row label="Tax Invoice No." value={isIssued ? job.tax_invoice_number : "— (not yet issued)"} />
        <Row label="Date" value={formatDate((job.tax_invoice_issued_at || new Date().toISOString()).slice(0,10))} />
        <Row label="Bill to" value={billTo} />
        {isInsurance ? (
          <>
            <Row label="Policy number" value={job.policy_number} />
            <Row label="Claim number" value={job.claim_number} />
            <Row label="Broker" value={job.broker} />
            <Row label="Quotation ref." value={job.quotation_number} />
            <Row label="Insured" value={job.client_name} />
          </>
        ) : (
          <>
            <Row label="ID / Company" value={job.client_id_number} />
            <Row label="Contact" value={job.contact_mobile} />
            <Row label="Email" value={job.email} />
            <Row label="Address" value={job.fitment_address} span={2} />
          </>
        )}
      </Grid>

      <SectionTitle>Vehicle Details</SectionTitle>
      <Grid>
        <Row label="Vehicle" value={job.vehicle_make_model} />
        <Row label="Year" value={job.vehicle_year} />
        <Row label="Registration" value={job.registration} />
        <Row label="VIN no" value={job.vin_number} />
        <Row label="Part code" value={job.part_code} span={2} />
        <Row label="Date fitted" value={formatDate(job.fitment_date)} />
        {!isInsurance && <Row label="Cause of loss" value={job.cause_of_loss} />}
      </Grid>

      <table className="w-full text-sm mt-6">
        <thead className="bg-[#edf2f4]">
          <tr className="text-left font-display uppercase tracking-widest text-[11px] text-[#3f4b52]">
            <th className="px-3 py-2">Description</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#eef2f4]">
          <tr><td className="px-3 py-2">Windscreen — {job.part_code || "—"}</td><td className="px-3 py-2 text-right">{money(calc.retail)}</td></tr>
          <tr><td className="px-3 py-2">Fitment</td><td className="px-3 py-2 text-right">{money(calc.fitment)}</td></tr>
          <tr><td className="px-3 py-2">Moulding</td><td className="px-3 py-2 text-right">{money(calc.moulding)}</td></tr>
          {(job.extra_line_items || []).filter((l) => !l.is_discount).map((l, i) => (
            <tr key={`ex-${i}`}><td className="px-3 py-2">{l.description || "Extra"}{l.qty && l.qty !== 1 ? ` (×${l.qty})` : ""}</td><td className="px-3 py-2 text-right">{money((l.qty || 1) * (l.unit_price || 0))}</td></tr>
          ))}
          {job.discount_pct > 0 && (
            <tr><td className="px-3 py-2 text-[#3f4b52]">Discount ({job.discount_pct}%)</td><td className="px-3 py-2 text-right text-[#3f4b52]">- {money(calc.grossLineSubtotal * job.discount_pct / 100)}</td></tr>
          )}
          {(job.extra_line_items || []).filter((l) => l.is_discount).map((l, i) => (
            <tr key={`dx-${i}`}><td className="px-3 py-2 text-[#3f4b52]">{l.description || "Discount"}</td><td className="px-3 py-2 text-right text-[#3f4b52]">- {money(Math.abs((l.qty || 1) * (l.unit_price || 0)))}</td></tr>
          ))}
          <tr><td className="px-3 py-2 text-[#3f4b52]">Subtotal (excl. VAT)</td><td className="px-3 py-2 text-right">{money(calc.subAfterDiscount)}</td></tr>
          <tr><td className="px-3 py-2 text-[#3f4b52]">VAT {job.vat_mode === "inclusive" ? "(incl.)" : job.vat_mode === "none" ? "(none — zero-rated)" : `(${job.vat_pct}%)`}</td><td className="px-3 py-2 text-right">{money(calc.vatAmount)}</td></tr>
          {isInsurance && (
            <>
              <tr><td className="px-3 py-2 text-[#3f4b52]">Inclusive subtotal</td><td className="px-3 py-2 text-right">{money(calc.inclusiveSubtotal)}</td></tr>
              <tr><td className="px-3 py-2 text-[#3f4b52]">Less: client excess ({job.excess_pct}%)</td><td className="px-3 py-2 text-right text-[#3f4b52]">- {money(calc.excessAmount)}</td></tr>
            </>
          )}
          <tr className="border-t-2 border-[#111619] bg-[#fafbfb]">
            <td className="px-3 py-3 font-display uppercase tracking-wide font-bold">
              {isInsurance ? "Total payable by insurer" : "Total due"}
            </td>
            <td className="px-3 py-3 text-right font-bold text-lg text-[#f28a16]">{money(totalDue)}</td>
          </tr>
        </tbody>
      </table>
      <Banking />
      <p className="mt-4 text-xs text-[#9aa6ad]">Tax Invoice issued in terms of the VAT Act. E&amp;OE.</p>
    </DocFrame>
  );
};

export const JobDocuments = ({ job, activeDoc = "job_card" }) => {
  const calc = calcJob(job);
  const docs = {
    job_card: <JobCard job={job} calc={calc} />,
    claim_form: <ClaimForm job={job} calc={calc} />,
    insurance_quotation: <InsuranceQuotation job={job} calc={calc} />,
    excess_invoice: <ExcessInvoice job={job} calc={calc} />,
    order_form: <OrderForm job={job} calc={calc} />,
    tax_invoice: <TaxInvoice job={job} calc={calc} />,
  };

  return (
    <div className="job-documents" data-active={activeDoc}>
      {Object.entries(docs).map(([key, node]) => (
        <div key={key} className={`doc-wrapper ${activeDoc === key ? "active" : "inactive"}`} data-doc-key={key}>
          {node}
        </div>
      ))}
    </div>
  );
};

export default JobDocuments;
